import { StatusPill } from '../../../design-system';
import { dueAging, statusLabel, waitingAging } from '../../../lib/taskStatus';
import { clientOf, typeOf, assigneeOf, initialsOf } from '../../../lib/taskViews';

// The list: one row per task under a group heading.
//
// ⚠️ THE ROW CARRIES THE DUE DATE **AND** HOW IT SITS AGAINST TODAY, which is
// the rule the old table already followed and the reason this is not just a
// prettier <ul>. "20 Sep" makes every reader do the arithmetic on every row;
// "4 days late" is the thing they came to find. The existing `dueAging` decides
// both the words and the tone, so the list cannot disagree with the table.

const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
  : '—');

function TaskListView({ groups, selectedIds, onToggle, onOpen }) {
  return (
    <div className="tv-list">
      {groups.map((g) => (
        <section className="tv-group" key={g.key}>
          <header className="tv-group-head">
            <span className="tv-group-name">{g.label}</span>
            <span className="tv-group-count">{g.tasks.length}</span>
          </header>

          {g.tasks.length === 0 ? (
            <p className="tv-group-empty">Nothing here.</p>
          ) : g.tasks.map((t) => {
            const age = dueAging(t);
            const client = clientOf(t);
            const type = typeOf(t);
            const who = assigneeOf(t);
            const waiting = waitingAging(t);
            return (
              <div
                key={t._id}
                /* ⚠️ The left edge is coloured by how the DUE DATE sits, not by
                   status. On a list grouped by status every row in a block
                   would otherwise carry the same stripe, spending the one
                   pre-attentive channel on the thing the heading already
                   says. */
                className={`tv-row tv-edge-${age.tone}`}
              >
                <input
                  type="checkbox"
                  className="tv-row-check"
                  checked={selectedIds.includes(t._id)}
                  onChange={() => onToggle(t._id)}
                  aria-label={`Select ${t.title}`}
                />

                <button type="button" className="tv-row-main" onClick={() => onOpen(t)}>
                  <span className="tv-row-title">{t.title}</span>
                  <span className="tv-row-meta">
                    <span className="tv-row-client">{client?.displayName || 'Internal'}</span>
                    {type && <><span className="tv-dot" aria-hidden="true">·</span>{type.name}</>}
                    {t.priority && t.priority !== 'medium' && (
                      <span className={`tv-chip tv-chip-${t.priority}`}>{t.priority}</span>
                    )}
                    {waiting && (
                      <span className="tv-chip tv-chip-waiting" title={t.waitingReason || 'Waiting on client'}>
                        waiting {waiting.label}
                      </span>
                    )}
                  </span>
                </button>

                <span className="tv-row-who">
                  <span className={`tv-avatar${who ? '' : ' tv-avatar-none'}`} aria-hidden="true">
                    {who ? initialsOf(who.name) : '–'}
                  </span>
                  <span className="tv-row-whoname">{who?.name || 'Unassigned'}</span>
                </span>

                <span className="tv-row-due">
                  <span className="tv-due-date">{fmtDate(t.dueDate)}</span>
                  <span className={`tv-due-age tv-tone-${age.tone}`}>{age.label}</span>
                </span>

                <span className="tv-row-status">
                  <StatusPill status={t.status} label={statusLabel(t.status)} />
                </span>
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
}

export default TaskListView;
